import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Identify.css";
import defaultImage from "../Components/Upload.jpg";
import { useTranslation } from "react-i18next";
import SpcIdentifies from "../Components/SpcIdentifies";

function Identify(props) {
  const { t } = useTranslation("global");
  const [image, setImage] = useState(defaultImage);
  const [file, setFile] = useState(null);
  const [responseText, setResponseText] = useState([
    ["", ""],
    ["", ""],
    ["", ""],
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0); // Track incorrect attempts
  const [showSuggestionInput, setShowSuggestionInput] = useState(false); // Show suggestion input after 3 attempts
  const [suggestedSpecies, setSuggestedSpecies] = useState(""); // Store suggested species
  const [isUploaded, setIsUploaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [langState, setLangState] = useState(
    localStorage.getItem("lang") || "en"
  );
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  useEffect(() => {
    const handleLanguageChange = () => {
      setLangState(localStorage.getItem("lang") || "en");
    };

    window.addEventListener("storage", handleLanguageChange);

    return () => {
      window.removeEventListener("storage", handleLanguageChange);
    };
  }, []);

  const lang = localStorage.getItem("lang");
  useEffect(() => {
    setLangState(lang || "en");
  }, [lang]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleClick = (id) => {
    props.setTree(id);
    window.location.href = "/tree";
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      setImage(reader.result);
      handleUpload(file);
    };

    if (file) {
      reader.readAsDataURL(file);
    }
    handleFileChange(event);
  };

  const handleUpload = (uploadedFile) => {
    const fileToUpload = uploadedFile || file;
    if (!fileToUpload) {
      setResponseText([{ message: "No Image Selected" }]);
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("file", fileToUpload);

    const userProfile = localStorage.getItem("user");
    formData.append("user", userProfile);

    axios
      .post(`${process.env.REACT_APP_API_URL}/predictjson`, formData)
      .then((response) => {
        const res = response.data;

        // CONDITION 1 ---------------------------
        if (res.condition === "1") {
          setResponseText([
            {
              class_bn: res.message,
              class_name: "",
              pred: "",
            },
          ]);
        }

        // CONDITION 2 ---------------------------
        else if (res.condition === "2") {
          setResponseText([
            {
              class_bn: `${res.best_match} (${res.translated_name}) - ${res.message}`,
              class_name: res.common_name,
              pred: "",
              related_images: [res.img1, res.img2, res.img3, res.img4],
            },
          ]);
        }

        // CONDITION 3 ---------------------------
        else if (res.condition === "3") {
          setResponseText([
            {
              class_bn: res.class_bn1,
              class_name: res.class_name1,
              pred: res.pred1,
            },
            {
              class_bn: res.class_bn2,
              class_name: res.class_name2,
              pred: res.pred2,
            },
            {
              class_bn: res.class_bn3,
              class_name: res.class_name3,
              pred: res.pred3,
            },
          ]);
        }

        setIsUploaded(true);
      })
      .catch(() => {
        setResponseText([{ message: "Error uploading image." }]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleNext = () => {
    if (incorrectAttempts < 2) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % responseText.length);
      setIncorrectAttempts(incorrectAttempts + 1);
    } else {
      setShowSuggestionInput(true); // Show input after 3 incorrect attempts
    }
  };

  const handleGiveFeedback = () => {
    setFeedbackGiven(true);
  };

  const handleSuggestionSubmit = () => {
    const email = localStorage.getItem("user") || "anonymous@example.com";

    axios
      .post(`${process.env.REACT_APP_API_URL}/update_identification`, {
        email,
        species: suggestedSpecies,
      })
      .then(() => {
        alert("Thank you for your suggestion!");
        setShowSuggestionInput(false); // Hide input after submission
        setSuggestedSpecies(""); // Clear input field
      })
      .catch((error) => {
        console.error("Error submitting suggestion:", error);
      });
  };

  return (
    <>
      <div className="identify">
        <div className="idn-left">
          <label
            className={`upload-label ${
              langState === "bn" ? "bangla-font" : ""
            }`}
          >
            {t("identify.upload")}
          </label>
          <div className="upload-container">
            <label htmlFor="fileInput">
              <img className="upload-logo" src={image} alt="Uploaded" />
              <input
                id="fileInput"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
            </label>
          </div>
          {isUploaded && (
            <div className="btn-container">
              <button onClick={handleRefresh} className="submit-btn">
                {t("identify.clear")}
              </button>
            </div>
          )}
        </div>
        <div className="idn-right">
          <div
            className={`upload-label ${
              langState === "bn" ? "bangla-font" : ""
            }`}
          >
            {t("identify.plant")}
          </div>
          {!isUploaded && (
            <div
              className={`instructions ${
                langState === "bn" ? "bangla-font" : ""
              }`}
            >
              {t("identify.instructions")}
              {"  "}Click <a href="/manual">here</a> for more instructions
            </div>
          )}
          <div className="pred-full">
            {isLoading ? (
              <div className="spinner"></div>
            ) : (
              isUploaded &&
              responseText.map(
                (prediction, index) =>
                  index === currentIndex && (
                    <div key={index}>
                      <div
                        className="pred-container"
                        onClick={() =>
                          prediction.pred !== "" &&
                          handleClick(parseInt(prediction.pred) + 1)
                        }
                      >
                        <div className="pred-class">{prediction.class_bn}</div>
                        <div className="pred-class">
                          {prediction.class_name}
                        </div>
                      </div>

                      {/* RELATED IMAGES BLOCK */}
                      {prediction.related_images && (
                        <div className="related-images">
                          <h4>Related Images</h4>
                          <div className="related-img-row">
                            {prediction.related_images.map(
                              (img, i) =>
                                img && (
                                  <img
                                    key={i}
                                    src={img}
                                    alt="related"
                                    className="related-thumb"
                                  />
                                )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
              )
            )}
          </div>
          {isUploaded && !feedbackGiven && (
            <div className="btn-container">
              <button
                onClick={handleGiveFeedback}
                style={{ width: "200px" }}
                className="submit-btn"
              >
                Give Feedback
              </button>
            </div>
          )}
          {isUploaded && feedbackGiven && !showSuggestionInput && (
            <div className="btn-container">
              <button onClick={handleRefresh} className="submit-btn">
                Correct
              </button>
              <button onClick={handleNext} className="submit-btn">
                Incorrect
              </button>
            </div>
          )}
          {showSuggestionInput && (
            <div className="suggestion-container">
              <p>
                If you are not satisfied with our result, please suggest the
                correct species:
              </p>
              <input
                type="text"
                value={suggestedSpecies}
                onChange={(e) => setSuggestedSpecies(e.target.value)}
                placeholder="Enter species name"
                className="suggestion-input"
              />
              <button
                onClick={handleSuggestionSubmit}
                className="identify-submit-btn"
              >
                Submit Suggestion
              </button>
            </div>
          )}
        </div>
      </div>
      <SpcIdentifies />
    </>
  );
}

export default Identify;
